/**
 * Suggestions under a text box: the one implementation behind `Combobox` and
 * the email box. `useSuggestions` holds which one is highlighted and handles
 * ↑ ↓ Enter Escape; `SuggestionList` draws them in a `Floating` panel (so a
 * dialog never clips it), grouped under section headers, with a muted hint on
 * the right. The text box keeps focus throughout (WAI-ARIA combobox).
 */

import { useEffect, useId, useState, type KeyboardEvent, type RefObject } from "react";
import { Floating } from "./floating";

export interface Suggestion {
  value: string;
  /** Shown instead of the value, e.g. a person's name. */
  label?: string | undefined;
  /** Muted text on the right, e.g. a sample value or an email. */
  hint?: string | undefined;
  /** Suggestions with the same section are listed together under it. */
  section?: string | undefined;
}

function sections(options: Suggestion[]): Array<[string, Suggestion[]]> {
  const out = new Map<string, Suggestion[]>();
  for (const o of options) out.set(o.section ?? "", [...(out.get(o.section ?? "") ?? []), o]);
  return [...out];
}

/** In section order, as listed: what ↑ ↓ walk through. */
const ordered = (options: Suggestion[]) => sections(options).flatMap(([, os]) => os);

export function useSuggestions(options: Suggestion[], onPick: (s: Suggestion) => void) {
  const id = useId();
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(-1);
  const flat = ordered(options);
  const expanded = open && flat.length > 0;
  const optionId = (i: number) => `${id}-o${i}`;

  useEffect(() => {
    if (expanded && active >= 0)
      document.getElementById(optionId(active))?.scrollIntoView?.({ block: "nearest" });
  });

  /** Open with `index` highlighted (-1: none). */
  const show = (index = -1) => (setOpen(true), setActive(index));
  const close = () => (setOpen(false), setActive(-1));

  /** True when it handled the key; the caller may act on Enter otherwise. */
  const onKeyDown = (e: KeyboardEvent<HTMLInputElement>): boolean => {
    if (e.key === "ArrowDown" || e.key === "ArrowUp") {
      e.preventDefault();
      if (!open) show(flat.length ? 0 : -1);
      else setActive((a) => Math.min(Math.max(a + (e.key === "ArrowDown" ? 1 : -1), 0), flat.length - 1));
      return true;
    }
    if (e.key === "Enter" && expanded && flat[active]) {
      e.preventDefault();
      onPick(flat[active]);
      return true;
    }
    if (e.key === "Escape" && expanded) {
      e.preventDefault();
      e.stopPropagation();
      close();
      return true;
    }
    return false;
  };

  return {
    id,
    open,
    expanded,
    active,
    setActive,
    show,
    close,
    onKeyDown,
    optionId,
    /** Spread on the text box. */
    inputProps: {
      role: "combobox" as const,
      "aria-expanded": expanded,
      "aria-controls": `${id}-list`,
      "aria-autocomplete": "list" as const,
      "aria-activedescendant": expanded && active >= 0 ? optionId(active) : undefined,
      autoComplete: "off",
      spellCheck: false,
    },
  };
}

export function SuggestionList({
  anchor,
  state,
  options,
  label,
  onPick,
  mono = false,
}: {
  anchor: RefObject<HTMLElement | null>;
  state: ReturnType<typeof useSuggestions>;
  options: Suggestion[];
  /** Names the list: "<label> suggestions". */
  label: string;
  onPick: (s: Suggestion) => void;
  mono?: boolean;
}) {
  if (!state.expanded) return null;
  let n = -1;
  return (
    <Floating anchor={anchor} onDismiss={state.close} className="overflow-auto p-1">
      <ul id={`${state.id}-list`} role="listbox" aria-label={`${label} suggestions`}>
        {sections(options).map(([section, os]) => (
          <li key={section} role="presentation">
            {section && (
              <div className="px-2 pt-1.5 pb-1 text-[11px] font-semibold tracking-[0.04em] text-faint uppercase">
                {section}
              </div>
            )}
            <ul role="group" aria-label={section || undefined}>
              {os.map((o) => {
                const i = ++n;
                return (
                  <li
                    key={o.value}
                    id={state.optionId(i)}
                    role="option"
                    aria-selected={i === state.active}
                    className={`flex cursor-pointer items-baseline justify-between gap-3 rounded-md px-2 py-1.5
                                text-[13px] ${i === state.active ? "bg-nav-hover text-ink" : "text-body"}`}
                    // Before the box's blur, so the pick lands first.
                    onMouseDown={(e) => (e.preventDefault(), onPick(o))}
                    onMouseMove={() => i !== state.active && state.setActive(i)}
                  >
                    <span className={`truncate ${mono ? "font-mono text-xs" : ""}`}>
                      {o.label ?? o.value}
                    </span>
                    {o.hint && (
                      <span className="max-w-[50%] shrink-0 truncate text-xs text-faint">{o.hint}</span>
                    )}
                  </li>
                );
              })}
            </ul>
          </li>
        ))}
      </ul>
    </Floating>
  );
}
